import ConfirmationAreaLCAPractitioner from 'components/v2/copy/ConfirmationAreaLCAPractitioner';
import ConfirmationAreaOwner from 'components/v2/copy/ConfirmationAreaOwner';
import ConsentCheckBox from 'components/v2/copy/ConsentCheckBox';
import DatePicker from 'components/v2/copy/DatePicker';
import WizardTextInputComponent from 'components/v2/epd-wizard-inputs/WizardTextInput';
import { CompanyContext } from 'contexts/CompanyContextProvider';
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import AuthService from 'services/AuthService';
import { ActionState, EPDCopyGroupStatus } from 'services/EpdClient';
import { EpdCopyGroupWizardModel, VerificationType } from 'services/EpdClient';
import styled from 'styled-components';
import { StyledForm } from 'styles/Styles.styled';
import { ActionFieldPanel, FieldPanelFullWidth, FieldPanelInput } from 'styles/v2/Styles.styled';
import { formatDate } from 'util/utils';

const WizardStep4: React.FunctionComponent<{
  groupData?: EpdCopyGroupWizardModel;
  onChangeEpdGroup: any;
}> = ({ groupData, onChangeEpdGroup }) => {
  const onChangeGroupInternal = (id: string, name: string, value: any) => {
    onChangeEpdGroup(id, name, value);
  };

  const versionDate = groupData?.versionDate ? new Date(groupData.versionDate) : undefined;
  const validityDate = groupData?.validityDate ? new Date(groupData.validityDate as Date) : undefined;
  const validiityDateError =
    validityDate && versionDate && validityDate.toDateString() !== versionDate.toDateString() && validityDate < versionDate
      ? 'Validity date must be later than version date'
      : undefined;

  const isChecked = groupData?.isAgreementAccepted;
  const isVerified: boolean = groupData?.status === EPDCopyGroupStatus.Verified;
  const isSentForPublish: boolean =
    groupData?.status === EPDCopyGroupStatus.SentForPublication ||
    groupData?.status === EPDCopyGroupStatus.SentForPublicationLicenseeAdmin;
  const isDeclined: boolean =
    groupData?.status === EPDCopyGroupStatus.DeclinedPublication ||
    groupData?.status === EPDCopyGroupStatus.DeclinedCopyByLicenseeAdmin;
  const { t } = useTranslation();
  const { company } = useContext(CompanyContext);
  const isOwner = AuthService.isCompanyOwner(company?.id as string);
  const companyEmail =
    company?.organizationEmail || groupData?.developersSource?.find((dev) => dev.isAccountManager)?.userEmail;
  const epdOwner: string =
    company?.name && companyEmail ? `${company?.name} | ${companyEmail}` : company?.name || companyEmail || '';
  const lcaPractitionerState =
    groupData?.groupState.lcaPractitionerState === ActionState.Enabled && !isOwner && !isSentForPublish
      ? ActionState.Enabled
      : ActionState.Hidden;
  return (
    <>
      <StyledForm>
        <ActionFieldPanel actionState={AuthService.isAdmin() ? ActionState.Enabled : ActionState.Hidden}>
          <DatePicker
            onChange={(e) => {
              if (!e.target.value) {
                onChangeGroupInternal(groupData?.id!, 'versionDate', null);
                return;
              }

              const inputDate = new Date(e.target.value as Date);
              const utcDate = new Date(Date.UTC(inputDate.getFullYear(), inputDate.getMonth(), inputDate.getDate()));
              e.target.value !== formatDate(groupData?.versionDate) &&
                onChangeGroupInternal(groupData?.id!, 'versionDate', utcDate);
            }}
            label={t('epdWizard.wizardStep4.properties.versionDate')}
            initialDate={versionDate}
            tooltip={t('epdWizard.wizardStep4.tooltips.versionDateCopyGroup')}
            tooltipPosition="right"
            required
          />
          <DatePicker
            onChange={(e) => {
              if (!e.target.value) {
                onChangeGroupInternal(groupData?.id!, 'validityDate', null);
                return;
              }
              const inputDate = new Date(e.target.value as Date);
              const utcDate = new Date(Date.UTC(inputDate.getFullYear(), inputDate.getMonth(), inputDate.getDate()));
              e.target.value !== formatDate(groupData?.validityDate) &&
                onChangeGroupInternal(groupData?.id!, 'validityDate', utcDate);
            }}
            label={t('epdWizard.wizardStep4.properties.validityDate.title')}
            initialDate={validityDate}
            tooltip={t('epdWizard.wizardStep4.tooltips.validityDate')}
            tooltipPosition="right"
            required
            error={validiityDateError}
          />
        </ActionFieldPanel>
        <FieldPanelFullWidth>
          <FieldPanelInput>
            <WizardTextInputComponent label={'EPD Owner'} value={epdOwner} name="epdOwner" disabled />
          </FieldPanelInput>
          <FieldPanelInput>
            <WizardTextInputComponent
              label={'Country'}
              value={company?.address?.country || ''}
              name="country"
              tooltip="The companies country of origin"
              tooltipPosition="right"
              disabled
            />
          </FieldPanelInput>
          <FieldPanelInput>
            <WizardTextInputComponent
              label={'Copyright'}
              value={t('epdWizard.wizardStep4.properties.protected')}
              name="copyright"
              tooltip="Indicates if there are copyrights on the EPD"
              tooltipPosition="right"
              disabled
            />
          </FieldPanelInput>
        </FieldPanelFullWidth>
        {groupData?.groupState.agreementAccepted != ActionState.Hidden && (
          <>
            <FieldPanelFullWidth
              state={
                (groupData?.verificationType == VerificationType.EPDVerification && (isVerified || isDeclined) && isOwner) ||
                (groupData?.verificationType != VerificationType.EPDVerification &&
                  (groupData?.groupState.sendToPublish == ActionState.Disabled ||
                    groupData?.groupState.sendToPublish == ActionState.Enabled))
                  ? ActionState.Enabled
                  : ActionState.Hidden
              }
            >
              <ConfirmationAreaOwner labelStyle={labelStyle} label={'Confirmation for publication'} />
              <CheckBoxPanel>
                <ConsentCheckBox
                  name="agree"
                  inputStyle={{ margin: '0 0.5rem 0 0' }}
                  checked={isChecked}
                  disable={groupData?.groupState.agreementAccepted == ActionState.Disabled}
                  onCheck={(e: React.ChangeEvent<HTMLInputElement>) => {
                    e.target.checked !== groupData?.isAgreementAccepted &&
                      onChangeGroupInternal(groupData?.id!, 'isAgreementAccepted', e.target.checked);
                  }}
                  label={'I have read and accept the service agreement'}
                />
              </CheckBoxPanel>
            </FieldPanelFullWidth>
            <FieldPanelFullWidth
              state={
                groupData?.verificationType == VerificationType.EPDVerification &&
                !isVerified &&
                isOwner &&
                !isSentForPublish &&
                !isDeclined
                  ? ActionState.Enabled
                  : ActionState.Hidden
              }
            >
              <FieldPanelInput>
                <WizardTextInputComponent
                  label={'Confirmation for publication'}
                  labelStyle={labelStyle}
                  value="Once the verification process is finished, 
              please accept service agreement in step 4 and send to publish your EPD copies."
                  name="confirmation"
                  disabled
                />
              </FieldPanelInput>
            </FieldPanelFullWidth>
            <FieldPanelFullWidth state={lcaPractitionerState}>
              <FieldPanelInput>
                <ConfirmationAreaLCAPractitioner labelStyle={labelStyle} label={'Confirmation for publication'} />
              </FieldPanelInput>
            </FieldPanelFullWidth>
          </>
        )}
      </StyledForm>
    </>
  );
};

const labelStyle: React.CSSProperties = {
  textTransform: 'uppercase',
  width: 'fit-content',
  fontWeight: '600',
};

const CheckBoxPanel = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  padding: 0.75rem;
  width: 100%;
  background-color: rgba(231, 81, 19, 0.1);
  border-radius: 2px;
`;

export default WizardStep4;
